<script setup>
import { useProfessionalDashboard } from "./professional_dashboard_business_logic";
import { fetchUserDataOnMount, useRouter } from "@/utils/vue_helpers";

fetchUserDataOnMount();

const {
  displayRole,
  roleIcon,
  quickActionGroups,
  hasQuickActions,
} = useProfessionalDashboard();

const { router } = useRouter();

function goToRoute(route) {
  router.value.push(route);
}
</script>

<template>
  <v-container fluid class="px-2 px-sm-3 px-md-4 py-3 py-md-4">
    <!-- Logged-in-as banner -->
    <v-sheet
      color="primary"
      dark
      rounded="lg"
      class="pa-4 pa-md-5 mb-4"
      :elevation="2"
    >
      <div class="d-flex align-center flex-wrap" :style="{ gap: '12px' }">
        <v-icon size="36" class="flex-shrink-0">{{ roleIcon }}</v-icon>
        <div class="flex-grow-1" :style="{ minWidth: '200px' }">
          <div class="text-overline text-uppercase mb-1" :style="{ opacity: 0.85 }">
            Professional session
          </div>
          <div class="text-h6 text-md-h5 font-weight-bold">
            Logged in as a {{ displayRole }}
          </div>
        </div>
        <v-chip
          small
          outlined
          dark
          class="font-weight-medium"
        >
          <v-icon left small>mdi-shield-account</v-icon>
          {{ displayRole }}
        </v-chip>
      </div>
    </v-sheet>

    <!-- Quick actions by category -->
    <template v-for="group in quickActionGroups">
      <div :key="group.id" class="mb-5">
        <div class="d-flex align-start flex-wrap mb-3" :style="{ gap: '8px' }">
          <v-icon :color="group.chipColor" class="mt-1">{{ group.icon }}</v-icon>
          <div>
            <div class="text-subtitle-1 font-weight-bold primary--text">
              {{ group.title }}
            </div>
            <div class="text-caption grey--text text--darken-1">
              {{ group.description }}
            </div>
          </div>
        </div>

        <v-row dense>
          <v-col
            v-for="(action, index) in group.actions"
            :key="`${group.id}-${index}`"
            cols="6"
            sm="4"
            md="3"
          >
            <v-card
              rounded="lg"
              outlined
              class="fill-height quick-action-card"
              @click="goToRoute(action.route)"
            >
              <v-card-text class="pa-3 pa-md-4 d-flex flex-column fill-height">
                <v-chip
                  x-small
                  :color="group.chipColor"
                  outlined
                  class="align-self-start mb-2 font-weight-medium"
                >
                  {{ action.categoryLabel }}
                </v-chip>
                <v-avatar :color="action.color" size="40" class="mb-3 white--text">
                  <v-icon dark>{{ action.icon }}</v-icon>
                </v-avatar>
                <div class="text-subtitle-2 font-weight-bold mb-1">
                  {{ action.title }}
                </div>
                <div class="text-caption grey--text flex-grow-1">
                  {{ action.subtitle }}
                </div>
                <v-icon small color="grey lighten-1" class="align-self-end mt-2">
                  mdi-arrow-right
                </v-icon>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </template>

    <v-alert
      v-if="!hasQuickActions"
      type="info"
      text
      dense
      class="mt-2"
    >
      No professional tools are currently assigned to your role. Contact the county if you believe this is an error.
    </v-alert>
  </v-container>
</template>

<style scoped>
.quick-action-card {
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.15s ease;
}

.quick-action-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
  transform: translateY(-2px);
}
</style>
